import { useState, useEffect, useCallback } from 'react';
import updateUserProfile from '../../../hooks/useUpdateUserProfile';
import { LawMakerList } from './LawMakerList';
import swal from 'sweetalert';
import {
  BodyBox,
  BodyBoxTitle,
  FilterBoxes,
  CheckContainer,
  ButtonAccountInformation,
  AccountInformationSpinner,
} from './Profile.Style';
import styled from 'styled-components';
import { parseKeywords } from '../../../utils/keywordUtils';

const KeywordSection = styled.div`
  margin: 1rem 0;
`;

const KeywordInput = styled.div`
  display: flex;
  margin-bottom: 1rem;
`;

const KeywordInputField = styled.input`
  flex-grow: 1;
  padding: 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid #ced4da;
`;

const KeywordsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const KeywordTag = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  padding: 0.5rem 0.75rem;
  margin: 0 1rem 1rem 0;
`;

const KeywordText = styled.span`
  font-style: ${(props) => (props.$isUnquoted ? 'italic' : 'normal')};
`;

const TagRemove = styled.span`
  cursor: pointer;
  font-size: 1rem;
  margin-right: 0.5rem;

  &:after {
    display: inline-block;
    content: '\\00d7';
  }
`;

const ClusterRadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column;
`;

const ClusterRadioOption = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.875rem;
  position: relative;
  padding: 0.25rem 0;
  font-weight: 600;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
  }

  span.radio-control {
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 0.5rem;
    border-radius: 50%;
    border: 3px solid #cccccc;
    background-color: #ffffff;
    position: relative;
  }

  span.radio-control::after {
    content: '';
    position: absolute;
    display: block;
    top: 50%;
    left: 50%;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: transparent;
    transition: all 0.2s ease;
    transform: translate(-50%, -50%);
  }

  input:checked + span.radio-control {
    border: 3px solid #0a819f;
  }

  input:checked + span.radio-control::after {
    background: #0a819f;
    border: 2px solid #ffffff;
  }

  input:focus + span.radio-control {
    outline: 2px solid #0a819f;
    outline-offset: 2px;
  }

  &:hover span.radio-control {
    border-color: #0a819f;
  }

  &:hover {
    color: #0a819f;
  }
`;

export const CoverageTopic = ({
  allTags,
  userInput,
  handleCheckboxClick,
  user,
  setUserInput,
  setUserContext,
}) => {
  const { updateCoverageTopics, updateProfileWithoutPush } =
    updateUserProfile();
  const [lawmakers, setLawmakers] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [clusterPreference, setClusterPreference] = useState('committee');

  const convertArrayToString = (array) => array.toString();
  const convertStringToArray = (string) => (string ? string.split(',') : []);

  const initializeUserData = useCallback(() => {
    if (user.user_lawmakers) {
      setLawmakers(JSON.parse(user.user_lawmakers));
    }

    // Load user-selected interests
    if (user.user_interests) {
      const userInterests = convertStringToArray(
        user.user_interests.toLowerCase()
      );
      setUserInput(userInterests);
    }

    // Load user keywords if they exist
    if (user.user_keywords) {
      try {
        const userKeywords = convertStringToArray(user.user_keywords);
        setKeywords(userKeywords);
      } catch (e) {
        // If parsing fails, start with empty array
        setKeywords([]);
      }
    }

    if (user.user_cluster_preference) {
      setClusterPreference(user.user_cluster_preference);
    }
  }, [user, setUserInput]);

  // Single effect to initialize user data when "user" changes
  useEffect(() => {
    initializeUserData();
  }, [initializeUserData]);

  // Add keyword handling functions
  const handleKeywordInputChange = (e) => {
    setKeywordInput(e.target.value);
  };

  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };

  const addKeyword = () => {
    const raw = keywordInput.trim();
    if (!raw) return;

    // Use shared parser so quoted phrases are preserved and unquoted words are split
    const inputTokens = parseKeywords(raw);
    if (!inputTokens || inputTokens.length === 0) {
      setKeywordInput('');
      return;
    }

    // Normalize, validate and preserve surrounding quotes for phrases
    const normalized = inputTokens
      .map((token) => {
        const isQuoted = token.startsWith('"') && token.endsWith('"');
        const inner = isQuoted ? token.slice(1, -1).trim() : token.trim();

        const valid =
          inner.length > 0 &&
          inner.length <= 30 &&
          !inner.includes(',') &&
          /^[a-z0-9'\- ]+$/i.test(inner);

        if (!valid) return null;
        return isQuoted ? `"${inner.toLowerCase()}"` : inner.toLowerCase();
      })
      .filter(Boolean);

    if (normalized.length === 0) {
      setKeywordInput('');
      return;
    }

    // Add only tokens that are not already present
    const tokensToAdd = normalized.filter((t) => !keywords.includes(t));
    if (tokensToAdd.length === 0) {
      setKeywordInput('');
      return;
    }

    setKeywords([...keywords, ...tokensToAdd]);
    setKeywordInput('');
  };

  const removeKeyword = (keywordToRemove) => {
    setKeywords(keywords.filter((keyword) => keyword !== keywordToRemove));
  };

  const getNewLocationCovered = () => {
    const locations = [];
    let index = 0;
    while (document.getElementsByName(`state_name__${index}`).length > 0) {
      const id =
        document.getElementsByName(`location_covered_id${index}`)[0].value ||
        null;
      locations.push({
        id,
        state_name: document.getElementsByName(`state_name__${index}`)[0].value,
        county: document.getElementsByName(`county__${index}`)[0].value,
        zip_code: document.getElementsByName(`zip_code__${index}`)[0].value,
      });
      index++;
    }
    return locations;
  };

  const getSelectedInterests = () => {
    return Array.from(document.getElementsByName('interest_checkbox[]'))
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.value)
      .join(',');
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    const updatedUser = {
      ...user,
      user_interests: getSelectedInterests(),
      user_lawmakers: JSON.stringify(lawmakers),
      user_keywords: convertArrayToString(keywords),
      user_cluster_preference: clusterPreference,
    };

    const locationCovered = getNewLocationCovered();
    const [coverageResult, profileResult] = await Promise.all([
      updateCoverageTopics(locationCovered),
      updateProfileWithoutPush(updatedUser),
    ]);

    if (coverageResult && profileResult) {
      await swal({
        title: 'Update',
        text: 'Profile updated successfully!',
        icon: 'success',
      });
      setUserContext('no');
    } else {
      swal('Profile Update Failed');
    }
    setLoading(false);
  };

  const formatDisplayName = (icon) => {
    if (icon === 'public_safety') return 'Public Safety';
    if (icon.startsWith('geo')) {
      return icon.substring(3).replace(/_/g, ' ').toUpperCase();
    }
    return icon.charAt(0).toUpperCase() + icon.slice(1);
  };

  return (
    <div>
      <BodyBox>
        <BodyBoxTitle>INTERESTS</BodyBoxTitle>
        <FilterBoxes>
          {allTags.map(({ svg, icon }, index) => (
            <CheckContainer key={index}>
              {formatDisplayName(icon)}
              <input
                value={icon}
                name="interest_checkbox[]"
                type="checkbox"
                checked={userInput.includes(icon)}
                onChange={() => handleCheckboxClick(icon)}
              />
              <span />
            </CheckContainer>
          ))}
        </FilterBoxes>
      </BodyBox>

      <BodyBox>
        <BodyBoxTitle>KEYWORDS</BodyBoxTitle>
        <p>
          Enter keywords or phrases of interest (letters, numbers, spaces,
          hyphens and apostrophes allowed). Press Enter to add.
        </p>
        <KeywordSection>
          <KeywordInput>
            <KeywordInputField
              value={keywordInput}
              onChange={handleKeywordInputChange}
              onKeyPress={handleKeywordKeyPress}
              placeholder="Enter a keyword or phrase"
              aria-label="Enter a keyword or phrase"
              maxLength={30}
            />
          </KeywordInput>

          <KeywordsList>
            {keywords.map((keyword, index) => {
              const isQuoted = /^".*"$/.test(keyword);
              return (
                <KeywordTag key={index}>
                  <TagRemove
                    onClick={() => removeKeyword(keyword)}
                    role="button"
                    aria-label={`Remove keyword ${keyword}`}
                  />
                  <KeywordText $isUnquoted={!isQuoted}>{keyword}</KeywordText>
                </KeywordTag>
              );
            })}
          </KeywordsList>
        </KeywordSection>
      </BodyBox>

      <LawMakerList Lawmakers={lawmakers} setLawmakers={setLawmakers} />

      <BodyBox>
        <BodyBoxTitle>CLUSTER PREFERENCE</BodyBoxTitle>
        <ClusterRadioGroup>
          <ClusterRadioOption>
            <input
              type="radio"
              name="clusterPreference"
              value="none"
              checked={clusterPreference === 'none'}
              onChange={() => setClusterPreference('none')}
            />
            <span className="radio-control"></span>
            None
          </ClusterRadioOption>
          <ClusterRadioOption>
            <input
              type="radio"
              name="clusterPreference"
              value="committee"
              checked={clusterPreference === 'committee'}
              onChange={() => setClusterPreference('committee')}
            />
            <span className="radio-control"></span>
            Committee
          </ClusterRadioOption>
          <ClusterRadioOption>
            <input
              type="radio"
              name="clusterPreference"
              value="bill"
              checked={clusterPreference === 'bill'}
              onChange={() => setClusterPreference('bill')}
            />
            <span className="radio-control"></span>
            Bill
          </ClusterRadioOption>
          <ClusterRadioOption>
            <input
              type="radio"
              name="clusterPreference"
              value="legislator"
              checked={clusterPreference === 'legislator'}
              onChange={() => setClusterPreference('legislator')}
            />
            <span className="radio-control"></span>
            Legislator
          </ClusterRadioOption>
          <ClusterRadioOption>
            <input
              type="radio"
              name="clusterPreference"
              value="topic"
              checked={clusterPreference === 'topic'}
              onChange={() => setClusterPreference('topic')}
            />
            <span className="radio-control"></span>
            Topic
          </ClusterRadioOption>
        </ClusterRadioGroup>
      </BodyBox>

      <BodyBox>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <ButtonAccountInformation
            type="button"
            onClick={handleUpdateProfile}
            disabled={loading}
          >
            {loading ? (
              <AccountInformationSpinner />
            ) : (
              'Save All'
            )}
          </ButtonAccountInformation>
        </div>
      </BodyBox>
    </div>
  );
};
